import { AnimatedSpritePack } from '../spritesheets/AnimatedSpritePack';
import { SpritesheetResult } from '../spritesheets/SpritesheetResult';
import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Brain, Download, Check, ChevronRight, Code2, FileSearch, Globe, Layers, Loader2, Search, Wrench } from 'lucide-react';
import { Markdown } from '../../components/Markdown';
import type { Block } from '../../types/api';
import type { ArtifactRef, ToolState } from './useRun';
import { ArtifactCard } from '../artifacts/ArtifactCard';
import { MediaCard } from '../media/MediaCard';
import { bytes, cx } from '../../lib/format';
import { API_URL, withAuth } from '../../lib/api';
import { Button } from '../../components/ui';
import { SecretsForm } from '../artifacts/SecretsForm';
import { partialJson } from './partialJson';

const ARTIFACT_TOOLS = new Set(['create_artifact', 'edit_artifact_file', 'write_artifact_file', 'delete_artifact_file', 'update_artifact_info']);
const TOOL_LABELS: Record<string, string> = {
  create_artifact: 'Creating artifact', edit_artifact_file: 'Editing source', write_artifact_file: 'Writing source', delete_artifact_file: 'Removing file',
  read_artifact: 'Reading artifact', list_artifacts: 'Looking up artifacts', validate_artifact: 'Testing artifact', update_artifact_info: 'Organizing artifact',
  return_uploaded_file: 'Preparing original download', list_files: 'Listing files', read_file: 'Reading files', analyze_table: 'Analyzing data', query_table: 'Analyzing data', search_project_files: 'Searching files',
  generate_prop_sheet: 'Creating prop asset sheet', generate_animated_character: 'Creating animated character sprites', remove_white_background: 'Removing white background locally', spritesheet_divider: 'Dividing spritesheet', remove_background: 'Removing background', wait_for_media: 'Waiting for assets', edit_image: 'Editing image', generate_3d_model: 'Building 3D model', add_media_to_artifact: 'Adding media to artifact',
  generate_image: 'Generating image', generate_video: 'Starting video render', generate_speech: 'Recording voiceover', generate_music: 'Composing music',
  search_past_conversations: 'Searching past chats', read_past_conversation: 'Reading past chat', find_project: 'Looking up projects', save_memory: 'Saving memory',
  web_search: 'Searching the web', web_fetch: 'Reading web page', code_execution: 'Running code', bash_code_execution: 'Running commands', text_editor_code_execution: 'Editing sandbox files',
};

function toolIcon(name: string) {
  if (name === 'web_search') return <Search size={14} />;
  if (name === 'web_fetch') return <Globe size={14} />;
  if (name.includes('code_execution')) return <Code2 size={14} />;
  if (ARTIFACT_TOOLS.has(name) || name === 'read_artifact' || name === 'validate_artifact') return <Layers size={14} />;
  if (name.includes('file') || name.includes('table') || name.includes('conversation')) return <FileSearch size={14} />;
  return <Wrench size={14} />;
}

interface Step { id: string; name: string; label: string; detail?: string; status: 'running' | 'done' | 'error'; summary?: string; sources?: { url: string; title: string }[]; code?: { path: string; text: string }[] }

const unescapeJson = (s: string) => s.replace(/\\(u[0-9a-fA-F]{4}|.)/g, (_m, c: string) => (c.length === 5 ? String.fromCharCode(parseInt(c.slice(1), 16)) : c === 'n' ? '\n' : c === 't' ? '\t' : c === 'r' ? '' : c));

/** The source a writing tool is producing, so the user can watch it — also while the JSON is still streaming. */
export function codeOf(name: string, input: any, partial?: string): { path: string; text: string }[] | undefined {
  if (!['create_artifact', 'write_artifact_file', 'edit_artifact_file'].includes(name)) return undefined;
  const draft = partial ? partialJson(partial) : null;
  if (draft && typeof draft === 'object') input = { ...input,...draft };
  if (input && Object.keys(input).length) {
    if (name === 'create_artifact') {
      // The model occasionally sends files as a JSON string or another shape; never let that crash the chat.
      let files: unknown = input.files;
      if (typeof files === 'string') { try { files = JSON.parse(files); } catch { files = []; } }
      if (!Array.isArray(files)) return undefined;
      return files.filter((f) => f && typeof f === 'object').map((f: any) => ({ path: String(f.path ?? 'File in progress'), text: typeof f.content === 'string' ? f.content : '' }));
    }
    if (name === 'write_artifact_file') return [{ path: String(input.path ?? 'file'), text: typeof input.content === 'string' ? input.content : '' }];
    return [{ path: String(input.path ?? 'file'), text: `- ${String(input.old_str ?? '').split('\n').join('\n- ')}\n+ ${String(input.new_str ?? '').split('\n').join('\n+ ')}` }];
  }
  if (!partial) return undefined;
  const out: { path: string; text: string }[] = [];
  const re = /"path"\s*:\s*"((?:[^"\\]|\\.)*)"[\s\S]*?"(?:content|new_str)"\s*:\s*"((?:[^"\\]|\\.)*)("|$)/g;
  for (const m of partial.matchAll(re)) out.push({ path: unescapeJson(m[1]), text: unescapeJson(m[2]) });
  if (!out.length) {
    // Give instant visual feedback while the model is still streaming the tool arguments.
    // Never interpret raw partial JSON as valid source until a content string begins.
    const path = partial.match(/\"path\"\s*:\s*\"((?:[^\"\\]|\\.)*)/);
    if (path) return [{ path: unescapeJson(path[1]), text: '' }];
  }
  return out.length ? out : undefined;
}

function resultText(r: Block | undefined): string {
  if (!r) return '';
  if (typeof r.content === 'string') return r.content;
  if (Array.isArray(r.content)) return r.content.map((c: any) => c.text ?? '').join(' ');
  return '';
}

/** Saved sprite inputs remain downloadable even when later processing fails. */
export function savedSpriteMediaIds(text: string): string[] {
  const ids=new Set<string>();
  for(const match of text.matchAll(/\b(?:source|transparent) media id:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gi))ids.add(match[1].toLowerCase());
  return [...ids];
}

function detailFor(name: string, input: any): string | undefined {
  if (!input) return undefined;
  if (name === 'web_search') return input.query;
  if (name === 'web_fetch') return input.url;
  if (input.path) return input.path;
  if (input.title) return input.title;
  if (input.query) return input.query;
  return undefined;
}

/** Builds steps from stored blocks (tool_use + tool_result pairs) merged with live tool states. */
export function buildSteps(blocks: Block[], tools: Record<string, ToolState>): Step[] {
  const results = new Map<string, Block>();
  for (const b of blocks) if (b.type === 'tool_result' || (/_tool_result$/.test(b.type))) results.set(b.tool_use_id, b);
  const steps: Step[] = [];
  for (const b of blocks) {
    if (b.type !== 'tool_use' && b.type !== 'server_tool_use' && b.type !== 'mcp_tool_use') continue;
    const live = tools[b.id];
    const r = results.get(b.id);
    let input = b.input;
    if ((!input || !Object.keys(input).length) && b._json) { try { input = JSON.parse(b._json); } catch { /* still streaming */ } }
    const isErr = r ? (r.is_error === true || (r.content && !Array.isArray(r.content) && typeof r.content === 'object' && String(r.content.type ?? '').endsWith('_error'))) : false;
    const status: Step['status'] = b._partial ? 'error' : live?.status ?? (r ? (isErr ? 'error' : 'done') : 'running');
    // Failed tool results contain actionable provider diagnostics. Never replace
    // them with a short live label or truncate them when restoring chat history.
    let summary = isErr ? resultText(r) || live?.summary : live?.summary;
    if (!summary && r) summary = b.type === 'server_tool_use' ? (Array.isArray(r.content) ? `${r.content.length} results` : isErr ? 'Failed' : 'Done') : resultText(r).split('\n')[0].slice(0, 140);
    const sources = r && r.type === 'web_search_tool_result' && Array.isArray(r.content) ? r.content.filter((x: any) => x.url).map((x: any) => ({ url: x.url, title: x.title ?? x.url })) : undefined;
    const label = b.type === 'mcp_tool_use' ? `${b.server_name}: ${b.name}` : live?.activity ?? TOOL_LABELS[b.name] ?? b.name;
    steps.push({ id: b.id, name: b.name, label, detail: detailFor(b.name, input), status, summary: b._partial ? 'Draft saved · not executed. Say “continue” to resume.' : summary, sources, code: codeOf(b.name, input, b._json ?? b._rawInput ?? b._invalidJson) ?? (b._json ? [{ path:'Live tool input',text:b._json }] : undefined) });
  }
  return steps;
}

function ToolSteps({ steps, running }: { steps: Step[]; running: boolean }) {
  // Open by default: the user should always see what Lumnos is doing and writing.
  const [open, setOpen] = useState(true);
  const active = steps.find((s) => s.status === 'running');
  const errors = steps.filter((s) => s.status === 'error').length;
  const head = active ? `${active.label}${active.detail ? ` · ${active.detail}` : ''}` : `${steps.length} step${steps.length === 1 ? '' : 's'}${errors ? ` · ${errors} failed` : ''}`;
  return (
    <div className={cx('steps', open && 'is-open')}>
      <button className="steps__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {active && running ? <Loader2 size={14} className="spin" /> : errors ? <AlertCircle size={14} className="text-warning" /> : <Check size={14} className="text-success" />}
        <span className="steps__title">{head}</span>
        <ChevronRight size={14} className="steps__chev" />
      </button>
      {open && (
        <ol className="steps__list">
          {steps.map((s) => (
            <li key={s.id} className={`step step--${s.status}`}>
              <span className="step__icon">{s.status === 'running' && running ? <Loader2 size={13} className="spin" /> : toolIcon(s.name)}</span>
              <div className="step__body">
                <div className="step__label">{s.label}{s.detail && <span className="step__detail"> {s.detail}</span>}</div>
                {s.summary && <div className="step__summary">{s.summary}</div>}
                {s.code?.map((c, ci) => (
                  <details key={ci} className="step__code" open={running || s.status === 'error'}>
                    <summary>{c.path} · {c.text ? `${c.text.split('\n').length} lines` : 'preparing source'}</summary>
                    <pre className="live-source">{c.text ? (c.text.length > 20000 ? c.text.slice(-20000) : c.text) : 'Writing file…'}</pre>
                  </details>
                ))}
                {s.sources && s.sources.length > 0 && (
                  <div className="sources">{s.sources.slice(0, 8).map((src) => <a key={src.url} href={src.url} target="_blank" rel="noopener noreferrer" className="source-chip">{hostOf(src.url)}</a>)}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const hostOf = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };

function Citations({ citations }: { citations: any[] }) {
  const seen = new Map<string, any>();
  for (const c of citations) if (c?.url && !seen.has(c.url)) seen.set(c.url, c);
  if (!seen.size) return null;
  return (
    <div className="citations">
      {[...seen.values()].map((c, i) => (
        <a key={c.url} href={c.url} target="_blank" rel="noopener noreferrer" className="source-chip" title={c.cited_text ?? c.title}>
          <span className="source-chip__n">{i + 1}</span>{c.title ? c.title.slice(0, 60) : hostOf(c.url)}
        </a>
      ))}
    </div>
  );
}

function Thinking({ text, live }: { text: string; live: boolean }) {
  const [open, setOpen] = useState(true);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (live && open) endRef.current?.scrollIntoView({ block: 'nearest' }); }, [text, live, open]);
  return (
    <div className={cx('thinking', open && 'is-open')}>
      <button className="steps__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {live ? <Loader2 size={14} className="spin" /> : <Brain size={14} />}
        <span className="steps__title">{live ? (text ? 'Reasoning stream · updating' : 'Waiting for reasoning summary') : 'Reasoning output'}</span>
        <ChevronRight size={14} className="steps__chev" />
      </button>
      {open && <div className="thinking__body">{text ? <Markdown text={text} /> : <span className="muted small">{live ? 'The provider has not sent a reasoning summary yet.' : 'The provider did not share a reasoning summary.'}</span>}<div ref={endRef} /></div>}
    </div>
  );
}

/** Extracts artifact references from stored blocks (create results carry the new id). */
export function artifactsFromBlocks(blocks: Block[]): { artifactId: string; title?: string }[] {
  const out = new Map<string, { artifactId: string; title?: string }>();
  const results = new Map(blocks.filter((b) => b.type === 'tool_result').map((b) => [b.tool_use_id, b]));
  for (const b of blocks) {
    if (b.type !== 'tool_use' || !ARTIFACT_TOOLS.has(b.name)) continue;
    const r = results.get(b.id);
    if (r?.is_error) continue;
    const id = b.input?.artifact_id ?? resultText(r).match(/Created artifact ([0-9a-f-]{36})/)?.[1];
    if (id) out.set(id, { artifactId: id, title: b.input?.title });
  }
  return [...out.values()];
}

/** A question from the ask_user tool: tick boxes per field and a Send button (active on the latest message only). */
export function Question({ input, onAnswer }: { input: any; onAnswer?: (text: string) => void }) {
  type Opt = { label: string; description?: string };
  const fields: { label: string; options: Opt[]; multiple?: boolean }[] = Array.isArray(input?.fields) && input.fields.length
    ? input.fields.filter((f: any) => typeof f?.label === 'string').map((f: any) => ({ ...f, options: Array.isArray(f.options) ? f.options : [] }))
    : [{ label: '', options: Array.isArray(input?.options) ? input.options : [] }];
  const [picked, setPicked] = useState<Record<number, string[]>>({});
  const [written, setWritten] = useState<Record<number, string>>({});
  if (!input?.question || !fields.length) return null;
  const toggle = (fi: number, label: string, multiple?: boolean) => setPicked((p) => {
    const cur = p[fi] ?? [];
    const next = multiple ? (cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]) : (cur[0] === label ? [] : [label]);
    return { ...p, [fi]: next };
  });
  const complete = fields.every((_, i) => (picked[i] ?? []).length > 0 || !!written[i]?.trim());
  const answer = fields.map((f, i) => `${f.label ? `${f.label}: ` : ''}${[...(picked[i] ?? []), written[i]?.trim()].filter(Boolean).join('; ')}`).join('\n');
  return (
    <div className="question" data-testid="question">
      <p className="question__text">{String(input.question)}</p>
      {fields.map((f, fi) => (
        <div key={fi} className="question__field">
          {f.label && <span className="question__label">{f.label}</span>}
          <div className="question__options" role={f.multiple ? 'group' : 'radiogroup'} aria-label={f.label || 'Choices'}>
            {f.options.map((o) => {
              const on = (picked[fi] ?? []).includes(o.label);
              return (
                <button key={o.label} type="button" role={f.multiple ? 'checkbox' : 'radio'} aria-checked={on} className={on ? 'question__opt is-on' : 'question__opt'} disabled={!onAnswer} onClick={() => toggle(fi, o.label, f.multiple)}>
                  <span className="question__tick" aria-hidden>{on && <Check size={12} strokeWidth={3} />}</span>
                  <span><strong>{o.label}</strong>{o.description && <small>{o.description}</small>}</span>
                </button>
              );
            })}
          </div>
          <label className="question__custom">
            <span className="muted small">{f.options.length ? 'Your own answer or additional details' : 'Your answer'}</span>
            <textarea className="input" aria-label={f.label ? `${f.label}: your answer` : 'Your answer'} placeholder="Type your answer…" rows={2} maxLength={10000} disabled={!onAnswer} value={written[fi] ?? ''} onChange={(e) => setWritten((prev) => ({ ...prev, [fi]: e.target.value }))} />
          </label>
        </div>
      ))}
      {onAnswer && (
        <div className="question__foot">
          <span className="muted small">Choose options, type answers, or add details to your choices.</span>
          <Button size="sm" variant="primary" disabled={!complete} onClick={() => onAnswer(answer)}>Send</Button>
        </div>
      )}
    </div>
  );
}

export function originalFilesOf(blocks: Block[]) {
  const results = new Map(blocks.filter((b) => b.type === 'tool_result' && !b.is_error).map((b) => [b.tool_use_id, b]));
  const files = new Map<string, { fileId: string; filename: string; sizeBytes: number }>();
  for (const b of blocks) {
    if (b.type !== 'tool_use' || b.name !== 'return_uploaded_file' || b._partial) continue;
    try {
      const file = JSON.parse(resultText(results.get(b.id))).originalFile;
      if (typeof file?.fileId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(file.fileId) && file.fileId === b.input?.file_id && typeof file.filename === 'string' && Number.isSafeInteger(file.sizeBytes) && file.sizeBytes >= 0) files.set(file.fileId, file);
    } catch { /* An unsuccessful or unfinished tool call has no download card. */ }
  }
  return [...files.values()];
}

export function AssistantContent({ blocks, tools = {}, liveArtifacts = [], running = false, onAnswer }: { blocks: Block[]; tools?: Record<string, ToolState>; liveArtifacts?: ArtifactRef[]; running?: boolean; onAnswer?: (text: string) => void }) {
  // Group consecutive tool/thinking blocks into one activity row; text renders inline.
  const askIds = new Set(blocks.filter((b) => b.type === 'tool_use' && (b.name === 'ask_user' || b.name === 'request_app_secrets')).map((b) => b.id));
  const groups: ({ kind: 'secrets'; input: any } | { kind: 'question'; input: any } | { kind: 'text'; block: Block } | { kind: 'tools'; blocks: Block[] } | { kind: 'thinking'; block: Block; live: boolean } | { kind: 'note'; text: string })[] = [];
  blocks.forEach((b, i) => {
    if (b.type === 'text') groups.push({ kind: 'text', block: b });
    else if (b.type === 'thinking') groups.push({ kind: 'thinking', block: b, live: running && i === blocks.length - 1 });
    else if (b.type === 'redacted_thinking') return;
    else if (b.type === 'compaction') {
      if (!b._partial && typeof b.content === 'string' && b.content.length) groups.push({ kind: 'note', text: 'Earlier context was summarized to fit the context window.' });
    }
    else if (b.type === 'tool_use' && b.name === 'ask_user') groups.push({ kind: 'question', input: b.input });
    else if (b.type === 'tool_use' && b.name === 'request_app_secrets' && b.input?.artifact_id) { groups.push({ kind: 'secrets', input: b.input }); return; }
    else if (b.type === 'tool_result' && askIds.has(b.tool_use_id)) return;
    else {
      const last = groups[groups.length - 1];
      if (last?.kind === 'tools') last.blocks.push(b);
      else groups.push({ kind: 'tools', blocks: [b] });
    }
  });
  const artifactIds = new Map<string, { artifactId: string; title?: string }>();
  for (const a of artifactsFromBlocks(blocks)) artifactIds.set(a.artifactId, a);
  for (const a of liveArtifacts) artifactIds.set(a.artifactId, { artifactId: a.artifactId, title: a.title });
  // Media created by generate_* tools: the tool_result names the media id.
  const mediaIds = new Set<string>();
  const resById = new Map(blocks.filter((b) => b.type === 'tool_result').map((b) => [b.tool_use_id, b]));
  for (const b of blocks) {
    if (b.type !== 'tool_use' || (!String(b.name).startsWith('generate_') && !['edit_image', 'remove_background', 'remove_white_background'].includes(String(b.name)))) continue;
    const text=resultText(resById.get(b.id));
    if(['generate_prop_sheet','generate_animated_character'].includes(String(b.name))){
      for(const savedId of savedSpriteMediaIds(text))mediaIds.add(savedId);
    }
    const id = text.match(/media id ([0-9a-f-]{36})/)?.[1];
    if (id) mediaIds.add(id);
  }

  const animationPacks=new Set<string>();
  for(const b of blocks)if(b.type==='tool_use'&&b.name==='generate_animated_character'){try{const result=JSON.parse(resultText(resById.get(b.id)));if(typeof result.animation_pack_id==='string'&&/^[0-9a-f-]{36}$/.test(result.animation_pack_id))animationPacks.add(result.animation_pack_id);}catch{/* pending or failed tool */}}
  const divisionJobs=new Set<string>();
  for(const b of blocks)if(b.type==='tool_use'&&(b.name==='spritesheet_divider'||b.name==='generate_prop_sheet')){
    try{const result=JSON.parse(resultText(resById.get(b.id)));if(typeof result.spritesheet_job_id==='string'&&/^[0-9a-f-]{36}$/.test(result.spritesheet_job_id))divisionJobs.add(result.spritesheet_job_id);}catch{/* tool still running or failed */}
  }
  return (
    <div className="assistant">
      {groups.map((g, i) => {
        if (g.kind === 'text') return <div key={i}><Markdown text={g.block.text ?? ''} />{g.block.citations?.length ? <Citations citations={g.block.citations} /> : null}</div>;
        if (g.kind === 'thinking') return <Thinking key={i} text={g.block.thinking ?? ''} live={g.live} />;
        if (g.kind === 'note') return <p key={i} className="muted small">{g.text}</p>;
        if (g.kind === 'secrets') return (
          <div key={i} className="question" data-testid="secrets-request">
            <p className="question__text">This app needs {Array.isArray(g.input.secrets) && g.input.secrets.length ? g.input.secrets.map((x: any) => x.name).join(', ') : 'some keys'}</p>
            <SecretsForm artifactId={g.input.artifact_id} only={Array.isArray(g.input.secrets) && g.input.secrets.length ? g.input.secrets.map((x: any) => x.name) : undefined} />
          </div>
        );
        if (g.kind === 'question') return <Question key={i} input={g.input} onAnswer={i === groups.length - 1 || groups.slice(i + 1).every((x) => x.kind !== 'question') ? onAnswer : undefined} />;
        const steps = buildSteps(g.blocks, tools);
        return steps.length ? <ToolSteps key={i} steps={steps} running={running} /> : null;
      })}
      {originalFilesOf(blocks).map((file) => (
        <a key={file.fileId} className="attach attach--static" href={withAuth(`${API_URL}/api/files/${file.fileId}/content`)} target="_blank" rel="noopener noreferrer" aria-label={`Download original ${file.filename}`}>
          <span className="attach__icon"><Download size={18} /></span>
          <span className="attach__meta"><span className="attach__name">{file.filename}</span><span className="attach__sub">Original upload · unchanged · {bytes(file.sizeBytes)}</span></span>
        </a>
      ))}
      {[...animationPacks].map(packId=><AnimatedSpritePack key={packId} packId={packId}/>)}
      {[...divisionJobs].map(jobId=><SpritesheetResult key={jobId} jobId={jobId}/>)}
      {mediaIds.size > 0 && <div className="media-cards">{[...mediaIds].map((id) => <MediaCard key={id} mediaId={id} />)}</div>}
      {artifactIds.size > 0 && (
        <div className="artifact-cards">
          {[...artifactIds.values()].map((a) => <ArtifactCard key={a.artifactId} artifactId={a.artifactId} fallbackTitle={a.title} />)}
        </div>
      )}
    </div>
  );
}
